import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, Map as MapIcon, PackageSearch, Printer, RefreshCw, Route, UserRound } from 'lucide-react';
import { useMutation, useQuery } from 'urql';
import { useNavigate } from 'react-router-dom';
import api from '@/api/axiosConfig.js';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import StatusChip from '@/components/common/StatusChip.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Textarea } from '@/components/ui/textarea.jsx';
import { OperationalCombobox } from '@/pages/Fojas/components/OperationalCombobox.jsx';
import GoogleRouteMap from '@/pages/Repartos/GoogleRouteMap.jsx';
import VehicleLoadGauge from '@/pages/Repartos/VehicleLoadGauge.jsx';
import { printRepartoSheet } from '@/pages/Repartos/repartoPrint.js';
import { choferesFilter } from '@/pages/Repartos/repartoActions.js';
import { useOperationalShortcuts } from '@/hooks/useOperationalShortcuts.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { CHOFERES_LIST } from '@/graphql/choferes.gql.js';
import { CREAR_REPARTO } from '@/graphql/repartos.gql.js';
import { fetchVehiculos } from '@/pages/Administracion/Flota/services/flota.service.js';
import {
  evaluateGuideEligibility,
  getGuideAddress,
  getGuideCashOnDelivery,
  getGuideDeclaredValue,
  getGuideLocality,
  getGuideNumber,
  getGuideNumericValue,
  getGuideRecipient,
  getGuideSearchText,
  isGuideRetry,
  mergeGuideSources,
  normalizeRepartoGuideState,
} from '@/pages/Repartos/repartoEligibility.js';

const QuickCreateChoferModal = lazy(() => import('@/pages/Fojas/components/QuickCreateChoferModal.jsx'));
const QuickCreateVehiculoModal = lazy(() => import('@/pages/Fojas/components/QuickCreateVehiculoModal.jsx'));

const GUIDE_FETCH_LIMIT = 200;
const lineas = [];
const ORDER_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'distancia', label: 'Por distancia (más cerca primero)' },
  { value: 'localidad', label: 'Por localidad' },
  { value: 'destinatario', label: 'Por destinatario' },
];

const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

function ShortcutChip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[rgba(var(--border),0.42)] bg-[rgba(var(--subpanel),0.82)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.7)]">
      {children}
    </span>
  );
}

function resolveSucursalInfo(auth) {
  const branch = auth?.sucursalActiva || auth?.sucursal || null;
  return {
    id: Number(branch?.id || branch?.sucursal_id || 0) || 0,
    nombre: branch?.nombre || branch?.descripcion || 'Sucursal activa',
  };
}

function toChoiceOption(item, config) {
  const id = String(item?.id ?? '');
  if (!id) return null;
  return {
    value: id,
    label: config.label(item),
    helperText: config.helperText?.(item) || '',
    searchText: config.searchText?.(item) || config.label(item),
  };
}

function getCompactStateTone(guide) {
  const state = normalizeRepartoGuideState(guide?.estado || guide?.estado_logistico);
  if (['RECIBIDA_DESTINO', 'CONFIRMADA_LOCAL', 'RECIBIDA'].includes(state)) return 'success';
  if (['RETENIDA_REPROGRAMABLE', 'REPROGRAMADA', 'RETENIDA'].includes(state)) return 'warning';
  if (['ENTREGADA', 'ANULADA', 'EN_REPARTO', 'PENDIENTE_RECANALIZACION'].includes(state)) return 'danger';
  return 'muted';
}

function sortSelectedGuides(guides, orderMode) {
  if (orderMode === 'localidad') {
    return [...guides].sort((a, b) =>
      getGuideLocality(a).localeCompare(getGuideLocality(b), 'es')
      || getGuideRecipient(a).localeCompare(getGuideRecipient(b), 'es')
      || String(a.id).localeCompare(String(b.id)));
  }
  if (orderMode === 'destinatario') {
    return [...guides].sort((a, b) =>
      getGuideRecipient(a).localeCompare(getGuideRecipient(b), 'es')
      || getGuideLocality(a).localeCompare(getGuideLocality(b), 'es')
      || String(a.id).localeCompare(String(b.id)));
  }
  return guides;
}

export default function RepartoNuevo() {
  const navigate = useNavigate();
  const auth = useAuth();
  const sucursalInfo = useMemo(() => resolveSucursalInfo(auth), [auth]);
  const sucursalId = sucursalInfo.id;

  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    chofer_id: '',
    unidad_id: '',
    observaciones: '',
  });
  const [guideRows, setGuideRows] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(false);
  const [guidesError, setGuidesError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [guideSearch, setGuideSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [showExcluded, setShowExcluded] = useState(false);
  const [onlyApt, setOnlyApt] = useState(true);
  const [onlyCod, setOnlyCod] = useState(false);
  const [onlyHighValue, setOnlyHighValue] = useState(false);
  const [onlyRetries, setOnlyRetries] = useState(false);
  const [sortMode, setSortMode] = useState('manual');
  const [routePreview, setRoutePreview] = useState(null);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState('');
  const [showGuidesModal, setShowGuidesModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [modalSelectedIds, setModalSelectedIds] = useState([]);
  const [showChoferModal, setShowChoferModal] = useState(false);
  const [showVehiculoModal, setShowVehiculoModal] = useState(false);
  const [vehiculos, setVehiculos] = useState([]);
  const [loadingVehiculos, setLoadingVehiculos] = useState(false);
  const choferSelectRef = useRef(null);
  const unidadSelectRef = useRef(null);
  const observacionesRef = useRef(null);
  const modalSearchRef = useRef(null);
  const choferContextRef = useRef(null);
  const unidadContextRef = useRef(null);
  const [, crear] = useMutation(CREAR_REPARTO);
  const choferFilter = choferesFilter(sucursalId);
  const [choferesResult] = useQuery({
    query: CHOFERES_LIST,
    variables: { page: 1, pageSize: 200, filter: choferFilter },
    pause: !choferFilter,
  });

  useEffect(() => {
    window.requestAnimationFrame(() => choferSelectRef.current?.focus?.());
  }, []);

  useEffect(() => {
    let ignore = false;
    async function loadVehiculos() {
      setLoadingVehiculos(true);
      try {
        const rows = await fetchVehiculos({ sucursal_id: sucursalId, limit: 200 });
        if (!ignore) setVehiculos(Array.isArray(rows) ? rows : []);
      } catch {
        if (!ignore) setVehiculos([]);
      } finally {
        if (!ignore) setLoadingVehiculos(false);
      }
    }
    if (sucursalId) loadVehiculos();
    return () => {
      ignore = true;
    };
  }, [sucursalId]);

  useEffect(() => {
    let ignore = false;
    async function loadGuides() {
      setLoadingGuides(true);
      setGuidesError('');
      try {
        const guiasResponse = await api.get('/api/guias', {
          params: { sucursal_id: sucursalId, limit: GUIDE_FETCH_LIMIT, page: 1 },
        });
        const baseRows = Array.isArray(guiasResponse?.data?.data) ? guiasResponse.data.data : [];
        if (!ignore) setGuideRows(mergeGuideSources(baseRows));
      } catch (error) {
        if (!ignore) {
          setGuideRows([]);
          setGuidesError(error?.response?.data?.message || error?.message || 'No se pudieron cargar las guias de reparto.');
        }
      } finally {
        if (!ignore) setLoadingGuides(false);
      }
    }
    if (sucursalId) loadGuides();
    return () => {
      ignore = true;
    };
  }, [sucursalId]);

  useEffect(() => {
    setModalSelectedIds(selectedIds);
  }, [selectedIds]);

  useEffect(() => {
    if (!showGuidesModal) return undefined;
    const frame = window.requestAnimationFrame(() => modalSearchRef.current?.focus?.());
    return () => window.cancelAnimationFrame(frame);
  }, [showGuidesModal]);

  const choferes = useMemo(() => choferesResult.data?.choferes?.rows || [], [choferesResult.data]);

  const lineaOptions = useMemo(
    () => lineas.map((item) => toChoiceOption(item, {
      label: (row) => `${row.codigo || row.id} · ${row.nombre}`,
      helperText: (row) => row.descripcion || `Linea ${row.codigo || row.id}`,
      searchText: (row) => [row.codigo, row.nombre, row.descripcion, row.id].filter(Boolean).join(' '),
    })).filter(Boolean),
    [],
  );

  void lineas;
  const choferOptions = useMemo(
    () => choferes.map((item) => toChoiceOption(item, {
      label: (row) => row.nombreCompleto || [row.apellido, row.nombre].filter(Boolean).join(', '),
      helperText: (row) => [row.nroDocumento, row.telefonoMovil, row.estado].filter(Boolean).join(' · '),
      searchText: (row) => [row.nombreCompleto, row.apellido, row.nombre, row.nroDocumento, row.cuilCuit, row.apodo].filter(Boolean).join(' '),
    })).filter(Boolean),
    [choferes],
  );

  const unidadOptions = useMemo(
    () => vehiculos.map((item) => toChoiceOption(item, {
      label: (row) => `${row.patente || row.id}${row.modelo ? ` · ${row.modelo}` : ''}`,
      helperText: (row) => [
        row.tipo,
        row.capacidad_m3 ? `${formatNumber(row.capacidad_m3, 1)} m³` : null,
        row.capacidad_kg ? `${formatNumber(row.capacidad_kg, 0)} kg` : null,
      ].filter(Boolean).join(' · '),
      searchText: (row) => [row.patente, row.interno, row.marca, row.modelo, row.tipo, row.id].filter(Boolean).join(' '),
    })).filter(Boolean),
    [vehiculos],
  );

  void lineaOptions;
  const selectedChofer = useMemo(
    () => choferes.find((item) => String(item.id) === String(form.chofer_id)) || null,
    [choferes, form.chofer_id],
  );
  const selectedUnidad = useMemo(
    () => vehiculos.find((item) => String(item.id) === String(form.unidad_id)) || null,
    [form.unidad_id, vehiculos],
  );

  const guidesWithEligibility = useMemo(
    () => guideRows.map((guide) => ({
      ...guide,
      eligibility: evaluateGuideEligibility(guide, { sucursalId }),
      searchText: getGuideSearchText(guide),
      locality: getGuideLocality(guide),
      recipient: getGuideRecipient(guide),
      address: getGuideAddress(guide),
      guideNumber: getGuideNumber(guide),
      declaredValue: getGuideDeclaredValue(guide),
      cashOnDelivery: getGuideCashOnDelivery(guide),
      bultos: getGuideNumericValue(guide, ['bultos'], 0),
      kilos: getGuideNumericValue(guide, ['kg_reales', 'kilos'], 0),
      volumen: getGuideNumericValue(guide, ['m3'], 0),
    })),
    [guideRows, sucursalId],
  );

  const localityOptions = useMemo(
    () => Array.from(new Set(guidesWithEligibility.map((guide) => guide.locality).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'es')),
    [guidesWithEligibility],
  );

  const filteredGuides = useMemo(
    () => guidesWithEligibility.filter((guide) => {
      if (onlyApt && !guide.eligibility.eligible) return false;
      if (guideSearch && !guide.searchText.includes(guideSearch.toLowerCase())) return false;
      if (zoneFilter && guide.locality !== zoneFilter) return false;
      if (onlyCod && guide.cashOnDelivery <= 0) return false;
      if (onlyHighValue && guide.declaredValue < 250000) return false;
      if (onlyRetries && !isGuideRetry(guide)) return false;
      return true;
    }),
    [guideSearch, guidesWithEligibility, onlyApt, onlyCod, onlyHighValue, onlyRetries, zoneFilter],
  );

  const eligibleGuides = useMemo(
    () => filteredGuides.filter((guide) => guide.eligibility.eligible),
    [filteredGuides],
  );

  const excludedGuides = useMemo(
    () => guidesWithEligibility.filter((guide) => !guide.eligibility.eligible),
    [guidesWithEligibility],
  );

  const selectedGuides = useMemo(() => {
    const selected = selectedIds
      .map((id) => guidesWithEligibility.find((guide) => Number(guide.id) === Number(id)))
      .filter(Boolean);
    return sortSelectedGuides(selected, sortMode);
  }, [guidesWithEligibility, selectedIds, sortMode]);

  useEffect(() => {
    if (sortMode === 'manual' || sortMode === 'distancia') return;
    setSelectedIds(selectedGuides.map((guide) => guide.id));
  }, [selectedGuides, sortMode]);

  const totals = useMemo(() => ({
    guias: selectedGuides.length,
    bultos: selectedGuides.reduce((acc, guide) => acc + Number(guide.bultos || 0), 0),
    kilos: selectedGuides.reduce((acc, guide) => acc + Number(guide.kilos || 0), 0),
    m3: selectedGuides.reduce((acc, guide) => acc + Number(guide.volumen || 0), 0),
    sinVolumen: selectedGuides.filter((guide) => !Number(guide.volumen)).length,
    valor: selectedGuides.reduce((acc, guide) => acc + Number(guide.declaredValue || 0), 0),
    cr: selectedGuides.reduce((acc, guide) => acc + Number(guide.cashOnDelivery || 0), 0),
    localidades: Array.from(new Set(selectedGuides.map((guide) => guide.locality))).filter(Boolean),
    bloqueantes: selectedGuides.filter((guide) => !guide.eligibility.eligible),
  }), [selectedGuides]);

  const submitReasons = useMemo(() => {
    const reasons = [];
    if (!form.chofer_id) reasons.push('Seleccione chofer');
    if (!form.unidad_id) reasons.push('Seleccione unidad');
    if (!selectedIds.length) reasons.push('Seleccione al menos una guia');
    if (selectedGuides.some((guide) => !guide.eligibility.eligible)) reasons.push('Hay guias seleccionadas que ya no son aptas');
    return reasons;
  }, [form.chofer_id, form.unidad_id, selectedGuides, selectedIds.length]);

  const canSubmit = submitReasons.length === 0;

  const modalTotals = useMemo(() => {
    const modalSelected = modalSelectedIds
      .map((id) => guidesWithEligibility.find((guide) => Number(guide.id) === Number(id)))
      .filter(Boolean);
    return {
      guias: modalSelected.length,
      bultos: modalSelected.reduce((acc, guide) => acc + Number(guide.bultos || 0), 0),
      kilos: modalSelected.reduce((acc, guide) => acc + Number(guide.kilos || 0), 0),
      valor: modalSelected.reduce((acc, guide) => acc + Number(guide.declaredValue || 0), 0),
      cr: modalSelected.reduce((acc, guide) => acc + Number(guide.cashOnDelivery || 0), 0),
    };
  }, [guidesWithEligibility, modalSelectedIds]);

  const toggleGuide = (guideId, nextChecked) => {
    setSelectedIds((prev) => {
      const exists = prev.includes(guideId);
      if ((nextChecked ?? !exists) && !exists) return [...prev, guideId];
      return prev.filter((id) => id !== guideId);
    });
  };

  const toggleModalGuide = (guideId, nextChecked) => {
    setModalSelectedIds((prev) => {
      const exists = prev.includes(guideId);
      if ((nextChecked ?? !exists) && !exists) return [...prev, guideId];
      return prev.filter((id) => id !== guideId);
    });
  };

  const moveSelectedGuide = (guideId, direction) => {
    setSortMode('manual');
    setSelectedIds((prev) => {
      const index = prev.findIndex((id) => Number(id) === Number(guideId));
      if (index < 0) return prev;
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const routeStale = Boolean(routePreview)
    && routePreview.paradas.map((stop) => Number(stop.guiaId)).join(',') !== selectedGuides.map((guide) => Number(guide.id)).join(',');
  const stopsByGuide = useMemo(
    () => new Map((routePreview?.paradas || []).map((stop) => [Number(stop.guiaId), stop])),
    [routePreview],
  );

  const requestRoute = async ({ reorder }) => {
    setRoutingError('');
    if (!selectedGuides.length) {
      setRoutingError('Seleccione al menos una guia para calcular el recorrido.');
      return null;
    }
    setRoutingLoading(true);
    try {
      const response = await api.post('/api/repartos/routing/preview', {
        sucursal_id: sucursalId,
        mantener_orden: !reorder,
        guias: selectedGuides.map((guide) => ({
          id: guide.id,
          numero_guia: guide.guideNumber,
          destinatario: guide.recipient,
          direccion: guide.address,
          localidad: guide.locality,
          ventana_entrega_desde: guide.ventana_entrega_desde,
          ventana_entrega_hasta: guide.ventana_entrega_hasta,
        })),
      });
      const data = response?.data?.data;
      if (!data?.paradas?.length) throw new Error('No se recibio una ruta valida.');
      setRoutePreview(data);
      if (reorder) {
        setSortMode('distancia');
        setSelectedIds(data.paradas.map((stop) => Number(stop.guiaId)));
      }
      return data;
    } catch (error) {
      setRoutingError(error?.response?.data?.message || error?.message || 'No se pudo calcular el recorrido.');
      return null;
    } finally {
      setRoutingLoading(false);
    }
  };

  const onSortChange = (value) => {
    if (value === 'distancia') {
      requestRoute({ reorder: true });
      return;
    }
    setSortMode(value);
  };

  const openRouteMap = async () => {
    const route = routePreview && !routeStale ? routePreview : await requestRoute({ reorder: false });
    if (route) setShowMapModal(true);
  };

  const printSheet = () => {
    printRepartoSheet({
      sucursal: sucursalInfo.nombre,
      fecha: form.fecha,
      chofer: selectedChofer?.nombreCompleto || [selectedChofer?.apellido, selectedChofer?.nombre].filter(Boolean).join(', '),
      unidad: selectedUnidad,
      guides: selectedGuides,
      totals,
      route: routeStale ? null : routePreview,
      observaciones: form.observaciones,
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    const selectedNow = selectedIds
      .map((id) => guidesWithEligibility.find((guide) => Number(guide.id) === Number(id)))
      .filter(Boolean);
    const invalid = selectedNow.filter((guide) => !guide.eligibility.eligible);
    if (invalid.length) {
      setSubmitError(`Hay guias que dejaron de ser aptas: ${invalid.map((guide) => guide.guideNumber).join(', ')}.`);
      return;
    }
    if (!canSubmit) {
      setSubmitError(submitReasons[0] || 'Completa los datos requeridos.');
      return;
    }

    const createRes = await crear({
      input: {
        fecha: form.fecha,
        sucursal_id: sucursalId,
        linea_id: null,
        chofer_id: form.chofer_id ? Number(form.chofer_id) : null,
        unidad_id: form.unidad_id ? Number(form.unidad_id) : null,
        observaciones: form.observaciones?.trim() || null,
        guia_ids: selectedNow.map((guide) => Number(guide.id)),
      },
    });

    const repartoId = Number(createRes.data?.crearReparto?.id);
    if (createRes.error || !repartoId) {
      setSubmitError(createRes.error?.message || 'No se pudo crear el reparto.');
      return;
    }

    navigate(`/cargas/repartos/${repartoId}`);
  };

  useOperationalShortcuts({
    enabled: true,
    requireSelectedLineaForGuides: false,
    isGuidesModalOpen: showGuidesModal,
    isChoferModalOpen: showChoferModal,
    isVehiculoModalOpen: showVehiculoModal,
    onOpenGuides: () => setShowGuidesModal(true),
    onOpenChoferQuickCreate: () => setShowChoferModal(true),
    onOpenVehiculoQuickCreate: () => setShowVehiculoModal(true),
    onBlockedShortcut: (message) => setSubmitError(message),
    primaryAction: {
      disabled: !canSubmit,
      onClick: () => document.getElementById('reparto-form-submit')?.click(),
      title: submitReasons.join(' · ') || 'Crear reparto',
    },
    choferContextRef,
    vehiculoContextRef: unidadContextRef,
  });

  return (
    <>
      <form onSubmit={onSubmit} className="mx-auto grid min-h-0 w-full max-w-[1800px] grid-cols-1 gap-2 overflow-x-hidden px-2 py-2 xl:h-[calc(100vh-5rem)] xl:grid-cols-[minmax(0,71fr)_minmax(290px,29fr)] xl:grid-rows-[48px_auto_minmax(0,1fr)]">
        <section className="col-span-1 flex min-w-0 items-center justify-between gap-2 border-b border-[rgba(var(--border),0.3)] px-2 xl:col-span-2">
          <h1 className="shrink-0 text-base font-semibold text-[rgb(var(--fg))]">Nuevo reparto</h1>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5 text-[11px] text-[rgba(var(--fg),0.72)]">
            <span className="truncate">Sucursal {sucursalInfo.nombre}</span><span aria-hidden="true">·</span>
            <time dateTime={form.fecha}>{form.fecha ? new Date(`${form.fecha}T00:00:00`).toLocaleDateString('es-AR') : ''}</time><span aria-hidden="true">·</span>
            <span>{formatNumber(totals.guias, 0)} guías</span><span aria-hidden="true">·</span><span>{formatNumber(totals.bultos, 0)} bultos</span>
          </div>
        </section>

        <section className="col-span-1 z-20 grid min-w-0 items-end gap-2 rounded-xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--panel),0.96)] p-2 sm:grid-cols-2 xl:col-span-2 xl:grid-cols-[150px_minmax(180px,1fr)_minmax(180px,1fr)_auto] xl:row-start-2">
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.12em] text-[rgba(var(--fg),0.56)]">Fecha</label>
                <Input type="date" value={form.fecha} onChange={(event) => setForm((prev) => ({ ...prev, fecha: event.target.value }))} className="h-9 text-[13px]" />
              </div>
              <div ref={choferContextRef} className="min-w-0">
                <OperationalCombobox
                  ref={choferSelectRef}
                  id="reparto-chofer"
                  label="Chofer"
                  value={form.chofer_id}
                  options={choferOptions}
                  loading={choferesResult.fetching}
                  loadingLabel="Cargando choferes..."
                  placeholder="Buscar por nombre, codigo o documento"
                  inputClassName="h-9 rounded-xl text-[13px]"
                  onChange={(value) => setForm((prev) => ({ ...prev, chofer_id: String(value) }))}
                  onCommitNext={() => unidadSelectRef.current?.focus?.()}
                />
                <button type="button" className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[rgba(var(--fg),0.64)] transition hover:text-[rgb(var(--fg))]" onClick={() => setShowChoferModal(true)}>
                  Alta rapida
                  <ShortcutChip>F7</ShortcutChip>
                </button>
              </div>
              <div ref={unidadContextRef} className="min-w-0">
                <OperationalCombobox
                  ref={unidadSelectRef}
                  id="reparto-unidad"
                  label="Unidad"
                  value={form.unidad_id}
                  options={unidadOptions}
                  loading={loadingVehiculos}
                  loadingLabel="Cargando unidades..."
                  placeholder="Buscar por patente, interno o descripcion"
                  inputClassName="h-9 rounded-xl text-[13px]"
                  onChange={(value) => setForm((prev) => ({ ...prev, unidad_id: String(value) }))}
                  onCommitNext={() => observacionesRef.current?.focus?.()}
                />
                <button type="button" className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[rgba(var(--fg),0.64)] transition hover:text-[rgb(var(--fg))]" onClick={() => setShowVehiculoModal(true)}>
                  Alta rapida
                  <ShortcutChip>F7</ShortcutChip>
                </button>
              </div>
              <details className="relative min-w-0">
                <summary className="flex h-9 cursor-pointer list-none items-center justify-center rounded-lg border border-[rgba(var(--border),0.55)] px-3 text-xs font-medium text-[rgb(var(--fg))]">Observaciones</summary>
                <div className="absolute right-0 top-10 z-50 w-72 rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgb(var(--panel))] p-2 shadow-xl">
                  <Textarea ref={observacionesRef} value={form.observaciones} onChange={(event) => setForm((prev) => ({ ...prev, observaciones: event.target.value }))} className="min-h-[82px] text-[13px]" placeholder="Observaciones operativas breves" />
                </div>
              </details>
            </section>

        <section className="col-span-1 min-h-0 min-w-0 space-y-2 xl:col-start-1 xl:row-start-3 xl:flex xl:flex-col">
          <section className="flex min-h-0 flex-1 flex-col rounded-xl border border-[rgba(var(--border),0.36)] bg-[rgba(var(--panel),0.96)] p-2">
            <div className="flex flex-col gap-2 border-b border-[rgba(var(--border),0.22)] pb-2">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Guías disponibles ({formatNumber(filteredGuides.length, 0)})</h2>
                <div className="flex gap-1.5">
                  <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setShowGuidesModal(true)}><PackageSearch size={14} />Seleccionar <ShortcutChip>F2</ShortcutChip></Button>
                  <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => window.location.reload()}><RefreshCw size={14} />Actualizar</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-1.5 xl:grid-cols-[minmax(140px,1fr)_minmax(120px,180px)_repeat(4,auto)]">
                <Input value={guideSearch} onChange={(event) => setGuideSearch(event.target.value)} className="h-9 text-[13px]" placeholder="Buscar guia, remito, destinatario o domicilio" />
                <select value={zoneFilter} title="Localidad de destino de la guía" aria-label="Filtrar por localidad de destino" onChange={(event) => setZoneFilter(event.target.value)} className="h-9 rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.9)] px-3 text-[13px] text-[rgb(var(--fg))]">
                  <option value="">Todas las localidades</option>
                  {localityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
                <button type="button" onClick={() => setOnlyCod((prev) => !prev)} className={`rounded-xl border px-3 text-[12px] font-medium ${onlyCod ? 'border-[rgba(var(--brand),0.4)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.62)] bg-[rgba(var(--panel),0.8)] text-[rgba(var(--fg),0.7)]'}`}>Con CR</button>
                <button type="button" onClick={() => setOnlyHighValue((prev) => !prev)} className={`rounded-xl border px-3 text-[12px] font-medium ${onlyHighValue ? 'border-[rgba(var(--brand),0.4)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.62)] bg-[rgba(var(--panel),0.8)] text-[rgba(var(--fg),0.7)]'}`}>VD alto</button>
                <button type="button" onClick={() => setOnlyRetries((prev) => !prev)} className={`rounded-xl border px-3 text-[12px] font-medium ${onlyRetries ? 'border-[rgba(var(--warning),0.4)] bg-[rgba(var(--warning),0.12)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.62)] bg-[rgba(var(--panel),0.8)] text-[rgba(var(--fg),0.7)]'}`}>Reintentos</button>
                <button type="button" onClick={() => setOnlyApt((prev) => !prev)} className={`rounded-xl border px-3 text-[12px] font-medium ${onlyApt ? 'border-[rgba(var(--success),0.4)] bg-[rgba(var(--success),0.12)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.62)] bg-[rgba(var(--panel),0.8)] text-[rgba(var(--fg),0.7)]'}`}>Solo aptas</button>
              </div>
            </div>

            {guidesError ? (
              <div className="mt-3 rounded-2xl border border-red-300/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">{guidesError}</div>
            ) : null}

            <div className="mt-2 min-h-[240px] flex-1 overflow-auto rounded-lg border border-[rgba(var(--border),0.28)]">
                <table className="w-full min-w-[680px] table-fixed border-collapse text-[12px]">
                  <colgroup>
                    <col className="w-[42px]" />
                    <col className="w-[112px]" />
                    <col className="w-[18%]" />
                    <col className="w-[14%]" />
                    <col className="w-[20%]" />
                    <col className="w-[58px]" />
                    <col className="w-[100px]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-[rgba(var(--panel),0.98)] text-left text-[10px] uppercase tracking-[0.12em] text-[rgba(var(--fg),0.5)] shadow-[0_1px_0_rgba(var(--border),0.22)]">
                    <tr>
                      <th className="px-2 py-2 text-center">Sel</th>
                      <th className="px-2 py-2">Guia</th>
                      <th className="px-2 py-2">Destinatario</th>
                      <th className="px-2 py-2">Localidad</th>
                      <th className="px-2 py-2">Domicilio</th>
                      <th className="px-2 py-2 text-right">Bultos</th>
                      <th className="px-2 py-2 text-right">CR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingGuides ? (
                      <tr><td colSpan={7} className="px-4 py-5 text-center text-sm text-[rgba(var(--fg),0.6)]">Cargando guias disponibles...</td></tr>
                    ) : !eligibleGuides.length ? (
                      <tr><td colSpan={7} className="px-4 py-5 text-center text-sm text-[rgba(var(--fg),0.6)]">No hay guias aptas para los filtros aplicados.</td></tr>
                    ) : eligibleGuides.map((guide) => {
                      const checked = selectedIds.includes(guide.id);
                      return (
                        <tr key={guide.id} className={checked ? 'border-t border-[rgba(var(--border),0.18)] bg-[rgba(var(--brand),0.08)]' : 'border-t border-[rgba(var(--border),0.18)] hover:bg-[rgba(var(--subpanel),0.48)]'}>
                          <td className="px-2 py-2 align-top text-center">
                            <input type="checkbox" checked={checked} onChange={(event) => toggleGuide(guide.id, event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-[rgba(var(--border),0.8)] accent-[rgb(var(--brand))]" />
                          </td>
                          <td className="px-2 py-2 align-top"><div className="font-semibold text-[rgb(var(--fg))]">{guide.guideNumber}</div><div className="mt-1 text-[11px] text-[rgba(var(--fg),0.56)]">{guide.remito || 'Sin remito'}</div></td>
                          <td className="px-2 py-2 align-top"><div className="truncate font-medium text-[rgb(var(--fg))]">{guide.recipient}</div><div className="mt-1 truncate text-[11px] text-[rgba(var(--fg),0.56)]">{guide.cliente_nombre || guide.cliente || guide.pagador_nombre || 'Sin pagador'}</div></td>
                          <td className="px-2 py-2 align-top"><div className="truncate font-medium text-[rgb(var(--fg))]">{guide.locality}</div></td>
                          <td className="px-2 py-2 align-top"><div className="line-clamp-2 text-[rgba(var(--fg),0.72)]">{guide.address || 'Sin domicilio informado'}</div></td>
                          <td className="px-2 py-2 text-right align-top tabular-nums">{formatNumber(guide.bultos, 0)}</td>
                          <td className="px-2 py-2 text-right align-top tabular-nums">{guide.cashOnDelivery ? formatCurrency(guide.cashOnDelivery) : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            <div className="mt-2">
              <button type="button" onClick={() => setShowExcluded((prev) => !prev)} className="inline-flex items-center gap-2 rounded-full border border-[rgba(var(--border),0.38)] px-3 py-1.5 text-[12px] font-medium text-[rgba(var(--fg),0.72)] transition hover:bg-[rgba(var(--subpanel),0.62)]">
                <AlertTriangle size={14} />
                {showExcluded ? 'Ocultar excluidas' : `Ver excluidas (${formatNumber(excludedGuides.length, 0)})`}
              </button>
            </div>

            {showExcluded ? (
              <div className="mt-3 overflow-hidden rounded-[20px] border border-[rgba(var(--border),0.28)]">
                <div className="border-b border-[rgba(var(--border),0.2)] bg-[rgba(var(--subpanel),0.6)] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">Guías que no pueden salir en reparto</div>
                <div className="max-h-[240px] overflow-auto">
                  <table className="w-full min-w-[920px] table-fixed border-collapse text-[12px]">
                    <colgroup>
                      <col className="w-[44px]" />
                      <col className="w-[140px]" />
                      <col className="w-[220px]" />
                      <col className="w-[180px]" />
                      <col className="w-[140px]" />
                      <col className="w-[220px]" />
                    </colgroup>
                    <thead className="sticky top-0 bg-[rgba(var(--panel),0.98)] text-left text-[10px] uppercase tracking-[0.12em] text-[rgba(var(--fg),0.5)]">
                      <tr>
                        <th className="px-2 py-2 text-center">Sel</th>
                        <th className="px-2 py-2">Guia</th>
                        <th className="px-2 py-2">Destinatario</th>
                        <th className="px-2 py-2">Localidad</th>
                        <th className="px-2 py-2">Estado</th>
                        <th className="px-2 py-2">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excludedGuides.map((guide) => (
                        <tr key={guide.id} className="border-t border-[rgba(var(--border),0.18)] bg-[rgba(var(--panel),0.8)]">
                          <td className="px-2 py-2 text-center align-top"><input type="checkbox" disabled className="mt-0.5 h-4 w-4 cursor-not-allowed rounded opacity-50" /></td>
                          <td className="px-2 py-2 align-top font-semibold">{guide.guideNumber}</td>
                          <td className="px-2 py-2 align-top">{guide.recipient}</td>
                          <td className="px-2 py-2 align-top">{guide.locality}</td>
                          <td className="px-2 py-2 align-top"><StatusChip label={guide.eligibility.state.replaceAll('_', ' ')} tone={getCompactStateTone(guide)} /></td>
                          <td className="px-2 py-2 align-top text-[rgba(var(--fg),0.66)]">{guide.eligibility.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </section>
        </section>

        <aside className="col-span-1 min-h-0 min-w-0 xl:col-start-2 xl:row-start-3">
          <div className="h-full min-h-0 xl:sticky xl:top-2">
            <section className="flex h-full min-h-0 flex-col rounded-xl border border-[rgba(var(--border),0.36)] bg-[rgba(var(--panel),0.97)] p-2">
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.62)]">Reparto actual · {totals.guias} guías | {totals.bultos} bultos | {formatCurrency(totals.cr)} CR</div>

              <div className="mt-1">
                <VehicleLoadGauge unidad={selectedUnidad} totals={totals} missingVolumeCount={totals.guias ? totals.sinVolumen : 0} fallbackShape="utilitario" storageKey="reparto" />
              </div>

              <div className="mt-2 grid grid-cols-4 gap-1.5 text-sm">
                <div className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.56)] px-3 py-2"><div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.5)]">Guías</div><div className="mt-1 font-semibold tabular-nums text-[rgb(var(--fg))]">{formatNumber(totals.guias, 0)}</div></div>
                <div className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.56)] px-3 py-2"><div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.5)]">Bultos</div><div className="mt-1 font-semibold tabular-nums text-[rgb(var(--fg))]">{formatNumber(totals.bultos, 0)}</div></div>
                <div className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.56)] px-3 py-2 col-span-2"><div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.5)]">A cobrar (CR)</div><div className="mt-1 font-semibold tabular-nums text-[rgb(var(--fg))]">{formatCurrency(totals.cr)}</div></div>
              </div>
              <div className="mt-2 flex items-center gap-2 text-[12px] text-[rgba(var(--fg),0.7)]">
                <UserRound size={14} className="shrink-0" />
                <span className="truncate">{selectedChofer?.nombreCompleto || 'Sin chofer asignado'}</span>
              </div>

              <div className="mt-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[rgba(var(--border),0.28)]">
                <div className="flex items-center justify-between gap-2 border-b border-[rgba(var(--border),0.2)] bg-[rgba(var(--subpanel),0.58)] px-3 py-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">Orden de entrega</span>
                  <select
                    value={sortMode}
                    onChange={(event) => onSortChange(event.target.value)}
                    disabled={routingLoading || !selectedGuides.length}
                    aria-label="Ordenar paradas"
                    className="h-8 max-w-[210px] rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.9)] px-2 text-[12px] text-[rgb(var(--fg))]"
                  >
                    {ORDER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                {routingLoading ? (
                  <div className="border-b border-[rgba(var(--border),0.2)] px-3 py-1.5 text-[11px] text-[rgba(var(--fg),0.62)]">Calculando distancias...</div>
                ) : routePreview && !routeStale ? (
                  <div className="border-b border-[rgba(var(--border),0.2)] px-3 py-1.5 text-[11px] text-[rgba(var(--fg),0.66)]">
                    Recorrido {routePreview.recorridoEstimado ? 'estimado ' : ''}{formatNumber(routePreview.distanciaMetros / 1000, 1)} km
                    {routePreview.duracionSegundos ? ` · ${formatNumber(routePreview.duracionSegundos / 60, 0)} min de manejo` : ''}
                  </div>
                ) : routePreview && sortMode === 'distancia' ? (
                  <button type="button" onClick={() => requestRoute({ reorder: true })} className="w-full border-b border-[rgba(var(--border),0.2)] bg-[rgba(var(--warning),0.1)] px-3 py-1.5 text-left text-[11px] font-medium text-[rgb(var(--fg))]">
                    La selección cambió. Recalcular orden por distancia
                  </button>
                ) : null}
                <div className="min-h-0 flex-1 overflow-auto">
                  {!selectedGuides.length ? (
                    <div className="px-3 py-4 text-center text-xs text-[rgba(var(--fg),0.58)]">Marque guías en la tabla o use Seleccionar (F2).</div>
                  ) : (
                    <ol className="divide-y divide-[rgba(var(--border),0.18)]">
                      {selectedGuides.map((guide, index) => {
                        const stop = routeStale ? null : stopsByGuide.get(Number(guide.id));
                        return (
                          <li key={guide.id} className="flex items-start gap-2 px-3 py-2">
                            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--brand),0.14)] text-[11px] font-semibold text-[rgb(var(--brand))]">{index + 1}</div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[13px] font-semibold text-[rgb(var(--fg))]">{guide.recipient}</div>
                              <div className="truncate text-[12px] text-[rgba(var(--fg),0.7)]">{guide.address || 'Sin domicilio'} · {guide.locality}</div>
                              <div className="truncate text-[11px] text-[rgba(var(--fg),0.52)]">
                                {guide.guideNumber}
                                {stop?.distanciaMetros ? ` · ${formatNumber(stop.distanciaMetros / 1000, 1)} km` : ''}
                                {stop?.llegadaEstimada ? ` · llega ${stop.llegadaEstimada}` : ''}
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <button type="button" aria-label="Subir" onClick={() => moveSelectedGuide(guide.id, -1)} className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-[rgba(var(--border),0.45)] text-[rgba(var(--fg),0.72)] hover:bg-[rgba(var(--subpanel),0.72)] disabled:opacity-40" disabled={index === 0}><ArrowUp size={12} /></button>
                              <button type="button" aria-label="Bajar" onClick={() => moveSelectedGuide(guide.id, 1)} className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-[rgba(var(--border),0.45)] text-[rgba(var(--fg),0.72)] hover:bg-[rgba(var(--subpanel),0.72)] disabled:opacity-40" disabled={index === selectedGuides.length - 1}><ArrowDown size={12} /></button>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" className="h-9 text-[12px]" disabled={!selectedGuides.length || routingLoading} onClick={openRouteMap}>
                  <MapIcon size={14} /> Ver recorrido
                </Button>
                <Button type="button" variant="outline" className="h-9 text-[12px]" disabled={!selectedGuides.length} onClick={printSheet}>
                  <Printer size={14} /> Hoja de ruta
                </Button>
              </div>

              {routingError ? <div className="mt-2 rounded-xl border border-[rgba(var(--danger),0.35)] bg-[rgba(var(--danger),0.08)] px-3 py-2 text-[12px] text-[rgb(var(--fg))]">{routingError}</div> : null}

              {totals.bloqueantes.length ? (
                <div className="mt-3 rounded-2xl border border-[rgba(var(--warning),0.4)] bg-[rgba(var(--warning),0.1)] px-3 py-3 text-sm text-[rgb(var(--fg))]">
                  <div className="font-semibold">Alertas bloqueantes</div>
                  <div className="mt-1">{totals.bloqueantes.map((guide) => `${guide.guideNumber}: ${guide.eligibility.reason}`).join(' · ')}</div>
                </div>
              ) : null}

              {submitError ? (
                <div className="mt-3 rounded-2xl border border-[rgba(var(--danger),0.4)] bg-[rgba(var(--danger),0.08)] px-3 py-3 text-sm text-[rgb(var(--fg))]">{submitError}</div>
              ) : null}

              <Button type="submit" className="mt-auto h-10 w-full shrink-0 text-sm" disabled={!canSubmit}>Crear reparto</Button>
              <button id="reparto-form-submit" type="submit" className="hidden" />
              {!canSubmit ? <div className="mt-2 text-sm text-[rgba(var(--fg),0.66)]">{submitReasons.join(' · ')}</div> : null}
            </section>
          </div>
        </aside>
      </form>

      <ModalBase
        isOpen={showGuidesModal}
        onClose={() => setShowGuidesModal(false)}
        labelledBy="modal-seleccion-guias-reparto"
        initialFocusRef={modalSearchRef}
        className="modal modal-content h-[88vh] w-full max-h-[88vh] overflow-hidden rounded-[18px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.985)] p-0 shadow-[0_30px_80px_rgba(var(--shadow),0.34)] text-[12px] focus-visible:outline-none"
        viewportClassName="modal-viewport modal-viewport--wide px-3 py-3 md:px-5"
        backdropClassName="modal-backdrop modal-backdrop--soft"
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="border-b border-[rgba(var(--border),0.22)] px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.5)]">Seleccion multiple</div>
                <h3 id="modal-seleccion-guias-reparto" className="text-[15px] font-semibold text-[rgb(var(--fg))]">Seleccionar guias</h3>
              </div>
              <div className="text-right text-[11px] text-[rgba(var(--fg),0.58)]">
                <div>{formatNumber(filteredGuides.length, 0)} visibles</div>
              </div>
            </div>
          </div>

          <div className="border-b border-[rgba(var(--border),0.22)] bg-[rgba(var(--subpanel),0.72)] px-4 py-3">
            <div className="grid gap-2 lg:grid-cols-[minmax(0,1.3fr)_190px_auto_auto_auto_auto]">
              <Input ref={modalSearchRef} value={guideSearch} onChange={(event) => setGuideSearch(event.target.value)} className="h-9 text-[13px]" placeholder="Buscar guia, remito o destinatario" />
              <select value={zoneFilter} title="Localidad de destino de la guía" aria-label="Filtrar por localidad de destino" onChange={(event) => setZoneFilter(event.target.value)} className="h-9 rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.9)] px-3 text-[13px] text-[rgb(var(--fg))]">
                <option value="">Todas las localidades</option>
                {localityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <button type="button" onClick={() => setOnlyCod((prev) => !prev)} className={`rounded-xl border px-3 text-[12px] font-medium ${onlyCod ? 'border-[rgba(var(--brand),0.4)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.62)] bg-[rgba(var(--panel),0.8)] text-[rgba(var(--fg),0.7)]'}`}>Con CR</button>
              <button type="button" onClick={() => setOnlyHighValue((prev) => !prev)} className={`rounded-xl border px-3 text-[12px] font-medium ${onlyHighValue ? 'border-[rgba(var(--brand),0.4)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.62)] bg-[rgba(var(--panel),0.8)] text-[rgba(var(--fg),0.7)]'}`}>VD alto</button>
              <button type="button" onClick={() => setOnlyRetries((prev) => !prev)} className={`rounded-xl border px-3 text-[12px] font-medium ${onlyRetries ? 'border-[rgba(var(--warning),0.4)] bg-[rgba(var(--warning),0.12)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.62)] bg-[rgba(var(--panel),0.8)] text-[rgba(var(--fg),0.7)]'}`}>Reintentos</button>
              <button type="button" onClick={() => setOnlyApt((prev) => !prev)} className={`rounded-xl border px-3 text-[12px] font-medium ${onlyApt ? 'border-[rgba(var(--success),0.4)] bg-[rgba(var(--success),0.12)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.62)] bg-[rgba(var(--panel),0.8)] text-[rgba(var(--fg),0.7)]'}`}>Solo aptas</button>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.92)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">Guias {formatNumber(modalTotals.guias, 0)}</span>
                <span className="rounded-full border border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.92)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">Bultos {formatNumber(modalTotals.bultos, 0)}</span>
                <span className="rounded-full border border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.92)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">Kg {formatNumber(modalTotals.kilos, 2)}</span>
                <span className="rounded-full border border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.92)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">VD {formatCurrency(modalTotals.valor)}</span>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setModalSelectedIds(eligibleGuides.map((guide) => guide.id))} disabled={!eligibleGuides.length}>Seleccionar filtradas</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setModalSelectedIds([])} disabled={!modalSelectedIds.length}>Limpiar</Button>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[1250px] table-fixed border-collapse text-[12px]">
              <colgroup>
                <col className="w-[42px]" />
                <col className="w-[140px]" />
                <col className="w-[220px]" />
                <col className="w-[150px]" />
                <col className="w-[220px]" />
                <col className="w-[64px]" />
                <col className="w-[76px]" />
                <col className="w-[70px]" />
                <col className="w-[110px]" />
                <col className="w-[110px]" />
                <col className="w-[110px]" />
                <col className="w-[160px]" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-[rgba(var(--panel),0.98)] text-left text-[10px] uppercase tracking-[0.12em] text-[rgba(var(--fg),0.5)] shadow-[0_1px_0_rgba(var(--border),0.22)]">
                <tr>
                  <th className="px-2 py-2 text-center">Sel</th>
                  <th className="px-2 py-2">Guia</th>
                  <th className="px-2 py-2">Destinatario</th>
                  <th className="px-2 py-2">Localidad</th>
                  <th className="px-2 py-2">Domicilio</th>
                  <th className="px-2 py-2 text-right">Bts</th>
                  <th className="px-2 py-2 text-right">Kg</th>
                      <th className="px-2 py-2 text-right">m³</th>
                  <th className="px-2 py-2 text-right">VD</th>
                  <th className="px-2 py-2 text-right">CR</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2">Motivo / alerta</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuides.map((guide) => {
                  const checked = modalSelectedIds.includes(guide.id);
                  const disabled = !guide.eligibility.eligible;
                  return (
                    <tr
                      key={guide.id}
                      onClick={() => !disabled && toggleModalGuide(guide.id)}
                      className={[
                        'border-t border-[rgba(var(--border),0.18)]',
                        checked ? 'bg-[rgba(var(--brand),0.08)]' : '',
                        disabled ? 'bg-[rgba(var(--subpanel),0.36)] opacity-80' : 'cursor-pointer hover:bg-[rgba(var(--subpanel),0.48)]',
                      ].join(' ')}
                    >
                      <td className="px-2 py-2 text-center align-top">
                        <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => toggleModalGuide(guide.id, event.target.checked)} onClick={(event) => event.stopPropagation()} className="mt-0.5 h-4 w-4 rounded border-[rgba(var(--border),0.8)] accent-[rgb(var(--brand))]" />
                      </td>
                      <td className="px-2 py-2 align-top"><div className="font-semibold text-[rgb(var(--fg))]">{guide.guideNumber}</div><div className="mt-1 text-[11px] text-[rgba(var(--fg),0.56)]">{guide.remito || 'Sin remito'}</div></td>
                      <td className="px-2 py-2 align-top"><div className="truncate font-medium text-[rgb(var(--fg))]">{guide.recipient}</div><div className="mt-1 truncate text-[11px] text-[rgba(var(--fg),0.56)]">{guide.cliente_nombre || guide.cliente || guide.pagador_nombre || 'Sin pagador'}</div></td>
                      <td className="px-2 py-2 align-top"><div className="truncate font-medium text-[rgb(var(--fg))]">{guide.locality}</div></td>
                      <td className="px-2 py-2 align-top">{guide.address || 'Sin domicilio informado'}</td>
                      <td className="px-2 py-2 text-right align-top tabular-nums">{formatNumber(guide.bultos, 0)}</td>
                      <td className="px-2 py-2 text-right align-top tabular-nums">{formatNumber(guide.kilos, 2)}</td>
                          <td className="px-2 py-2 text-right align-top tabular-nums">{guide.volumen ? formatNumber(guide.volumen, 2) : '-'}</td>
                      <td className="px-2 py-2 text-right align-top tabular-nums">{guide.declaredValue ? formatCurrency(guide.declaredValue) : '-'}</td>
                      <td className="px-2 py-2 text-right align-top tabular-nums">{guide.cashOnDelivery ? formatCurrency(guide.cashOnDelivery) : '-'}</td>
                      <td className="px-2 py-2 align-top"><StatusChip label={guide.eligibility.state.replaceAll('_', ' ')} tone={getCompactStateTone(guide)} /></td>
                      <td className="px-2 py-2 align-top text-[rgba(var(--fg),0.66)]">{guide.eligibility.eligible ? (isGuideRetry(guide) ? 'Reintento operativo' : 'Apta para reparto') : guide.eligibility.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="border-t border-[rgba(var(--border),0.22)] bg-[rgba(var(--panel),0.98)] px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-[11px] text-[rgba(var(--fg),0.58)]">Escape cierra. El modal mantiene seleccion multiple y totales en vivo.</div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowGuidesModal(false)}>Cancelar</Button>
                <Button
                  type="button"
                  onClick={() => {
                    setSelectedIds(modalSelectedIds.filter((id) => {
                      const guide = guidesWithEligibility.find((row) => Number(row.id) === Number(id));
                      return guide?.eligibility?.eligible;
                    }));
                    setShowGuidesModal(false);
                  }}
                >
                  Confirmar seleccion
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModalBase>

      <ModalBase
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        labelledBy="modal-recorrido-reparto"
        className="modal modal-content w-full max-w-[1100px] overflow-hidden rounded-[18px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.985)] p-0 shadow-[0_30px_80px_rgba(var(--shadow),0.34)] focus-visible:outline-none"
        viewportClassName="modal-viewport modal-viewport--wide px-3 py-3 md:px-5"
        backdropClassName="modal-backdrop modal-backdrop--soft"
      >
        <div className="flex items-center justify-between gap-3 border-b border-[rgba(var(--border),0.22)] px-4 py-3">
          <div>
            <h3 id="modal-recorrido-reparto" className="text-[15px] font-semibold text-[rgb(var(--fg))]">Recorrido del reparto</h3>
            {routePreview ? (
              <div className="text-[12px] text-[rgba(var(--fg),0.62)]">
                Sale de {routePreview.origen?.nombre || sucursalInfo.nombre} · {formatNumber(routePreview.paradas.length, 0)} paradas · {formatNumber(routePreview.distanciaMetros / 1000, 1)} km
                {routePreview.recorridoEstimado ? ' (distancia en línea recta)' : ''}
              </div>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={printSheet}><Printer size={14} /> Imprimir</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowMapModal(false)}>Cerrar</Button>
          </div>
        </div>
        {routePreview ? (
          <div className="grid gap-3 p-3 lg:grid-cols-[minmax(0,1fr)_300px]">
            <GoogleRouteMap route={routePreview} className="h-[62vh] min-h-[320px]" />
            <ol className="max-h-[62vh] space-y-1.5 overflow-auto pr-1">
              {routePreview.paradas.map((stop) => (
                <li key={stop.guiaId} className="flex gap-2 rounded-xl border border-[rgba(var(--border),0.24)] px-2.5 py-2">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--brand))] text-[11px] font-semibold text-white">{stop.orden}</div>
                  <div className="min-w-0 text-[12px]">
                    <div className="truncate font-semibold text-[rgb(var(--fg))]">{stop.destinatario || stop.numeroGuia}</div>
                    <div className="line-clamp-2 text-[rgba(var(--fg),0.68)]">{stop.direccion}</div>
                    <div className="text-[11px] text-[rgba(var(--fg),0.52)]">
                      {stop.numeroGuia}
                      {stop.distanciaMetros ? ` · ${formatNumber(stop.distanciaMetros / 1000, 1)} km` : ''}
                      {stop.llegadaEstimada ? ` · llega ${stop.llegadaEstimada}` : ''}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </ModalBase>

      {showChoferModal ? (
        <Suspense fallback={null}>
          <QuickCreateChoferModal
            isOpen={showChoferModal}
            onClose={() => setShowChoferModal(false)}
            empresaId={auth?.user?.empresaId || auth?.tenant?.empresaId || auth?.tenant?.id || null}
            sucursalId={sucursalId}
            onCreated={(chofer) => setForm((prev) => ({ ...prev, chofer_id: String(chofer.id) }))}
          />
        </Suspense>
      ) : null}

      {showVehiculoModal ? (
        <Suspense fallback={null}>
          <QuickCreateVehiculoModal
            isOpen={showVehiculoModal}
            onClose={() => setShowVehiculoModal(false)}
            sucursalId={sucursalId}
            onCreated={(vehiculo) => {
              setVehiculos((prev) => [vehiculo, ...prev.filter((item) => String(item.id) !== String(vehiculo.id))]);
              setForm((prev) => ({ ...prev, unidad_id: String(vehiculo.id) }));
            }}
          />
        </Suspense>
      ) : null}
    </>
  );
}
